(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-kalaboost-video-product]';
  const TRACK_SELECTOR = '[data-kalaboost-vp-track]';
  const SLIDE_SELECTOR = '[data-kalaboost-vp-slide]';
  const CARD_SELECTOR = '[data-kalaboost-vp-card]';
  const PLAY_SELECTOR = '[data-kalaboost-vp-play]';
  const MUTE_SELECTOR = '[data-kalaboost-vp-mute]';
  const PREV_SELECTOR = '[data-kalaboost-vp-prev]';
  const NEXT_SELECTOR = '[data-kalaboost-vp-next]';
  const MODAL_SELECTOR = '[data-kalaboost-vp-modal]';
  const MODAL_MEDIA_SELECTOR = '[data-kalaboost-vp-modal-media]';
  const MODAL_PRODUCT_SELECTOR = '[data-kalaboost-vp-modal-product]';
  const MODAL_MUTE_SELECTOR = '[data-kalaboost-vp-modal-mute]';
  const MODAL_CLOSE_SELECTOR = '[data-kalaboost-vp-modal-close]';
  const MEDIA_SLOT_SELECTOR = '[data-kalaboost-vp-media-slot]';

  const instances = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getGap(track) {
    return Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
  }

  function getScrollAmount(track) {
    const slide = track.querySelector(SLIDE_SELECTOR);
    if (!slide) return track.clientWidth;
    return slide.getBoundingClientRect().width + getGap(track);
  }

  function setMutedIcons(button, isMuted) {
    if (!button) return;

    const mutedIcon = button.querySelector('.kalaboost-video-product__icon--muted');
    const unmutedIcon = button.querySelector('.kalaboost-video-product__icon--unmuted');
    if (mutedIcon) mutedIcon.classList.toggle('is-hidden', !isMuted);
    if (unmutedIcon) unmutedIcon.classList.toggle('is-hidden', isMuted);

    const muteLabel = button.dataset.muteLabel || 'Couper le son';
    const unmuteLabel = button.dataset.unmuteLabel || 'Activer le son';
    button.setAttribute('aria-label', isMuted ? unmuteLabel : muteLabel);
  }

  function setCardMutedState(card, isMuted) {
    if (!card) return;
    card.classList.toggle('is-muted', isMuted);
    setMutedIcons(card.querySelector(MUTE_SELECTOR), isMuted);
  }

  function pauseCardVideos(section) {
    section.querySelectorAll(`${CARD_SELECTOR} video`).forEach((video) => {
      video.pause();
      const card = video.closest(CARD_SELECTOR);
      if (card) card.classList.remove('is-playing');
      const playButton = card?.querySelector(PLAY_SELECTOR);
      if (playButton) playButton.classList.remove('is-hidden');
    });
  }

  class KalaboostVideoProduct {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector(TRACK_SELECTOR);
      this.modal = section.querySelector(MODAL_SELECTOR);
      this.modalMedia = section.querySelector(MODAL_MEDIA_SELECTOR);
      this.modalProduct = section.querySelector(MODAL_PRODUCT_SELECTOR);
      this.modalMute = section.querySelector(MODAL_MUTE_SELECTOR);
      this.prevButton = section.querySelector(PREV_SELECTOR);
      this.nextButton = section.querySelector(NEXT_SELECTOR);
      this.activeCard = null;
      this.activeSlot = null;
      this.activeVideo = null;
      this.lastFocus = null;
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onVolumeChange = this.onVolumeChange.bind(this);
      this.scrollRaf = 0;
    }

    init() {
      this.section.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeyDown);

      if (this.track) {
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateNavState();
      }

      this.section.querySelectorAll(`${CARD_SELECTOR} video`).forEach((video) => {
        const card = video.closest(CARD_SELECTOR);
        setCardMutedState(card, video.muted);
      });
    }

    destroy() {
      this.closeModal({ restoreFocus: false });

      if (this.scrollRaf) {
        window.cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = 0;
      }

      this.section.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeyDown);

      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
      }

      this.section.querySelectorAll(`${CARD_SELECTOR} video`).forEach((video) => {
        video.pause();
        video.removeEventListener('volumechange', this.onVolumeChange);
      });
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = window.requestAnimationFrame(() => {
        this.scrollRaf = 0;
        this.updateNavState();
      });
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.isModalOpen()) {
        event.preventDefault();
        this.closeModal();
      }
    }

    onVolumeChange(event) {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement)) return;
      this.syncMuteUi(video.muted);
    }

    isModalOpen() {
      return Boolean(this.modal && !this.modal.hasAttribute('hidden'));
    }

    updateNavState() {
      if (!this.track) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const left = this.track.scrollLeft;
      const epsilon = 2;

      if (this.prevButton) {
        const disabled = left <= epsilon;
        this.prevButton.disabled = disabled;
        this.prevButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      if (this.nextButton) {
        const disabled = left >= maxScroll - epsilon;
        this.nextButton.disabled = disabled;
        this.nextButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }
    }

    scrollByDirection(direction) {
      if (!this.track) return;

      const amount = getScrollAmount(this.track) * direction;
      this.track.scrollBy({
        left: amount,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    selectSlide(slide) {
      if (!this.track || !slide) return;

      slide.scrollIntoView({
        inline: 'start',
        block: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    syncMuteUi(isMuted) {
      if (this.activeCard) setCardMutedState(this.activeCard, isMuted);
      if (this.modalMute) {
        this.modal.classList.toggle('is-muted', isMuted);
        setMutedIcons(this.modalMute, isMuted);
      }
    }

    openModal(card) {
      if (!this.modal || !this.modalMedia || !card) return;

      const slot = card.querySelector(MEDIA_SLOT_SELECTOR);
      const video = slot?.querySelector('video');
      if (!slot || !video) return;

      if (this.isModalOpen() && this.activeCard === card) return;

      if (this.isModalOpen()) {
        this.closeModal({ restoreFocus: false });
      }

      pauseCardVideos(this.section);

      this.lastFocus = document.activeElement;
      this.activeCard = card;
      this.activeSlot = slot;
      this.activeVideo = video;

      if (this.modalProduct) {
        this.modalProduct.replaceChildren();
        const product = card.querySelector('.kalaboost-video-product__product');
        if (product && product.tagName === 'A') {
          const clone = product.cloneNode(true);
          clone.classList.add('kalaboost-video-product__product--modal');
          this.modalProduct.appendChild(clone);
        }
      }

      this.modalMedia.appendChild(video);
      video.addEventListener('volumechange', this.onVolumeChange);
      video.muted = false;
      this.syncMuteUi(false);

      this.modal.removeAttribute('hidden');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('kalaboost-vp-modal-open');

      const closeButton = this.modal.querySelector(MODAL_CLOSE_SELECTOR);
      if (closeButton) closeButton.focus();

      video.play().catch(() => {
        video.muted = false;
        this.syncMuteUi(false);
      });
    }

    closeModal({ restoreFocus = true } = {}) {
      if (!this.modal || !this.isModalOpen()) {
        this.activeCard = null;
        this.activeSlot = null;
        this.activeVideo = null;
        return;
      }

      const video = this.activeVideo;
      if (video) {
        video.pause();
        video.removeEventListener('volumechange', this.onVolumeChange);
        if (this.activeSlot) this.activeSlot.appendChild(video);
        if (this.activeCard) {
          this.activeCard.classList.remove('is-playing');
          const playButton = this.activeCard.querySelector(PLAY_SELECTOR);
          if (playButton) playButton.classList.remove('is-hidden');
          setCardMutedState(this.activeCard, video.muted);
        }
      }

      if (this.modalProduct) this.modalProduct.replaceChildren();

      this.modal.setAttribute('hidden', '');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('kalaboost-vp-modal-open');

      const focusTarget = this.lastFocus;
      this.activeCard = null;
      this.activeSlot = null;
      this.activeVideo = null;
      this.lastFocus = null;

      if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus();
      }
    }

    toggleModalMute() {
      if (!this.activeVideo) return;
      this.activeVideo.muted = !this.activeVideo.muted;
      this.syncMuteUi(this.activeVideo.muted);
    }

    onClick(event) {
      const prevButton = event.target.closest(PREV_SELECTOR);
      if (prevButton && this.section.contains(prevButton)) {
        event.preventDefault();
        this.scrollByDirection(-1);
        return;
      }

      const nextButton = event.target.closest(NEXT_SELECTOR);
      if (nextButton && this.section.contains(nextButton)) {
        event.preventDefault();
        this.scrollByDirection(1);
        return;
      }

      const modalClose = event.target.closest(MODAL_CLOSE_SELECTOR);
      if (modalClose && this.section.contains(modalClose)) {
        event.preventDefault();
        this.closeModal();
        return;
      }

      const modalMute = event.target.closest(MODAL_MUTE_SELECTOR);
      if (modalMute && this.section.contains(modalMute)) {
        event.preventDefault();
        this.toggleModalMute();
        return;
      }

      const playButton = event.target.closest(PLAY_SELECTOR);
      if (playButton && this.section.contains(playButton) && !playButton.closest(MODAL_SELECTOR)) {
        event.preventDefault();
        this.openModal(playButton.closest(CARD_SELECTOR));
        return;
      }

      const muteButton = event.target.closest(MUTE_SELECTOR);
      if (muteButton && this.section.contains(muteButton) && !muteButton.closest(MODAL_SELECTOR)) {
        event.preventDefault();
        const card = muteButton.closest(CARD_SELECTOR);
        const video = card?.querySelector('video');
        if (!video) return;
        video.muted = !video.muted;
        setCardMutedState(card, video.muted);
      }
    }
  }

  function initSection(section) {
    if (!section || instances.has(section)) return;

    const instance = new KalaboostVideoProduct(section);
    instance.init();
    instances.set(section, instance);
  }

  function destroySection(section) {
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  }

  function initAll(root) {
    (root || document).querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR) || event.target.closest(SECTION_SELECTOR);
    if (section) destroySection(section);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest(SLIDE_SELECTOR);
    if (!slide) return;

    const section = slide.closest(SECTION_SELECTOR);
    const instance = section ? instances.get(section) : null;
    if (instance) instance.selectSlide(slide);
  });
})();
